/* eslint-disable no-param-reassign */
import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit'
import {useSelector} from 'react-redux'
import {http} from 'hiya-common/src/utils/axios'

import {PROB_GIFT_API, PROB_GIFT_RANK_PAGE_SIZE} from './constants'
import {resolveNextPageOffset} from './mapApi'
import type {
  HomeResponse,
  LuckyResponse,
  OpenChestResponse,
  ProbGiftState,
  RankResponse,
  ScoreDetailResponse,
  TaskResponse,
} from './types'

type ApiEnvelope<T> = {
  ret: number
  data: T
}

const emptyHome: HomeResponse = {
  phase: 0,
  countdown_sec: 0,
  prize_pool: 0,
  my_score: 0,
  my_rank: 0,
  rank_display: '',
  gap_to_top3: 0,
  estimate_coins: 0,
  estimate_ratio: 0,
  divide_ratio: [],
  divide_ratio_text: '',
  can_join: false,
  config_id: 0,
  warmup_st: 0,
  act_st: 0,
  act_et: 0,
  review_et: 0,
  b_gift_id: 0,
  room_id: 0,
  score_tasks: [],
  err_msg: '',
}

const emptyRank: RankResponse = {
  list: [],
  mine: {
    rank: 0,
    mid: 0,
    name: '',
    avatar: '',
    score: 0,
    estimate_coins: 0,
    gap_to_top3: 0,
  },
  top: 3,
  offset: 0,
  limit: PROB_GIFT_RANK_PAGE_SIZE,
  more: false,
  err_msg: '',
}

const emptyTask: TaskResponse = {
  my_score: 0,
  milestones: [],
  reward_preview_icons: [],
  err_msg: '',
}

const emptyLucky: LuckyResponse = {
  list: [],
  err_msg: '',
}

const emptyScoreDetail: ScoreDetailResponse = {
  list: [],
  offset: 0,
  limit: 20,
  more: false,
  err_msg: '',
}

export const getHome = createAsyncThunk(
  'GlGift/getHome',
  async (_p: void, store: any) => {
    const resp = await http.post<ApiEnvelope<HomeResponse>>(
      PROB_GIFT_API.home,
      {
        ...store.getState().env.deviceinfo,
      },
      {store, xcErrorToast: true}
    )
    return resp.data.data
  }
)

export type GetRankParams = {
  offset?: number
  limit?: number
}

export const getRank = createAsyncThunk(
  'GlGift/getRank',
  async (arg: GetRankParams | undefined, store: any) => {
    const offset = arg?.offset ?? 0
    const limit = arg?.limit ?? PROB_GIFT_RANK_PAGE_SIZE
    const resp = await http.post<ApiEnvelope<RankResponse>>(
      PROB_GIFT_API.rank,
      {
        ...store.getState().env.deviceinfo,
        offset,
        limit,
      },
      {store, xcErrorToast: true}
    )
    return {
      ...resp.data.data,
      requestOffset: offset,
    }
  }
)

export const getTask = createAsyncThunk(
  'GlGift/getTask',
  async (_p: void, store: any) => {
    const resp = await http.post<ApiEnvelope<TaskResponse>>(
      PROB_GIFT_API.task,
      {
        ...store.getState().env.deviceinfo,
      },
      {store, xcErrorToast: true}
    )
    return resp.data.data
  }
)

export const getLucky = createAsyncThunk(
  'GlGift/getLucky',
  async (_p: void, store: any) => {
    const resp = await http.post<ApiEnvelope<LuckyResponse>>(
      PROB_GIFT_API.lucky,
      {
        ...store.getState().env.deviceinfo,
      },
      {store, xcErrorToast: true}
    )
    return resp.data.data
  }
)

export type GetScoreDetailParams = {
  offset: number
  limit: number
}

export const getScoreDetail = createAsyncThunk(
  'GlGift/getScoreDetail',
  async (arg: GetScoreDetailParams, store: any) => {
    const resp = await http.post<ApiEnvelope<ScoreDetailResponse>>(
      PROB_GIFT_API.scoreDetail,
      {
        ...store.getState().env.deviceinfo,
        offset: arg.offset,
        limit: arg.limit,
      },
      {store, xcErrorToast: true}
    )
    return {
      ...resp.data.data,
      offset: arg.offset,
      limit: arg.limit,
    }
  }
)

export const openChest = createAsyncThunk(
  'GlGift/openChest',
  async (arg: {level: number}, store: any) => {
    const resp = await http.post<ApiEnvelope<OpenChestResponse>>(
      PROB_GIFT_API.openChest,
      {
        ...store.getState().env.deviceinfo,
        level: arg.level,
      },
      {store, xcErrorToast: true}
    )
    return resp.data.data
  }
)

const initialState: ProbGiftState = {
  home: emptyHome,
  rank: emptyRank,
  task: emptyTask,
  lucky: emptyLucky,
  scoreDetail: emptyScoreDetail,
  openingChest: false,
  scoreDetailLoading: false,
  rankLoading: false,
  rankRefreshing: false,
}

export const glGiftSlice = createSlice({
  name: 'GlGift',
  initialState,
  reducers: {
    updatePrizePool(state, action: PayloadAction<number>) {
      const next = Number(action.payload)
      if (!Number.isFinite(next)) return
      state.home.prize_pool = Math.max(0, Math.floor(next))
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getHome.fulfilled, (state, action) => {
      state.home = action.payload as HomeResponse
    })
    builder.addCase(getRank.pending, (state, action) => {
      const arg = action.meta.arg as GetRankParams | undefined
      state.rankLoading = true
      state.rankRefreshing = (arg?.offset ?? 0) === 0
    })
    builder.addCase(getRank.fulfilled, (state, action) => {
      const payload = action.payload as RankResponse & {
        requestOffset: number
      }
      state.rankLoading = false
      state.rankRefreshing = false
      if (payload.requestOffset === 0) {
        state.rank.list = payload.list
        state.rank.mine = payload.mine
        state.rank.top = payload.top
      } else {
        state.rank.list = state.rank.list.concat(payload.list)
      }
      state.rank.offset = resolveNextPageOffset(
        payload.requestOffset,
        payload.list.length,
        payload.offset
      )
      state.rank.limit = payload.limit
      state.rank.more = payload.more
      state.rank.err_msg = payload.err_msg
    })
    builder.addCase(getRank.rejected, (state) => {
      state.rankLoading = false
      state.rankRefreshing = false
    })
    builder.addCase(getTask.fulfilled, (state, action) => {
      state.task = action.payload as TaskResponse
    })
    builder.addCase(getLucky.fulfilled, (state, action) => {
      state.lucky = action.payload as LuckyResponse
    })
    builder.addCase(getScoreDetail.pending, (state) => {
      state.scoreDetailLoading = true
    })
    builder.addCase(getScoreDetail.fulfilled, (state, action) => {
      const payload = action.payload as ScoreDetailResponse & {
        offset: number
      }
      state.scoreDetailLoading = false
      if (payload.offset === 0) {
        state.scoreDetail.list = payload.list
      } else {
        state.scoreDetail.list = state.scoreDetail.list.concat(payload.list)
      }
      state.scoreDetail.err_msg = payload.err_msg
    })
    builder.addCase(getScoreDetail.rejected, (state) => {
      state.scoreDetailLoading = false
    })
    builder.addCase(openChest.pending, (state) => {
      state.openingChest = true
    })
    builder.addCase(openChest.fulfilled, (state) => {
      state.openingChest = false
    })
    builder.addCase(openChest.rejected, (state) => {
      state.openingChest = false
    })
  },
})

export const {updatePrizePool} = glGiftSlice.actions

export default glGiftSlice.reducer

export const useProbGiftState = () =>
  useSelector((state: any) => state.GlGift as ProbGiftState) ?? initialState
